import type { CSSProperties } from 'react';
import { Category } from '../types';

export type CategoryTreeNode = Omit<Category, 'children' | 'level'> & {
  parent?: number | null;
  level: number;
  children: CategoryTreeNode[];
};

const byDefaultOrder = (a: Category, b: Category) => {
  const orderDiff = (a.display_order ?? 0) - (b.display_order ?? 0);
  if (orderDiff !== 0) return orderDiff;
  return (a.name_en || '').localeCompare(b.name_en || '');
};

export const categoryDropdownOverflowStyle: CSSProperties = {
  maxHeight: 'min(70vh, 520px)',
  overflowY: 'auto',
  overflowX: 'hidden',
};

export const buildCategoryTree = (categories: Category[]): CategoryTreeNode[] => {
  const nodes = new Map<number, CategoryTreeNode>();

  categories.forEach(category => {
    nodes.set(category.id, {
      ...category,
      parent: category.parent ?? null,
      level: 0,
      children: [],
    });
  });

  const roots: CategoryTreeNode[] = [];
  nodes.forEach(node => {
    if (node.parent && nodes.has(node.parent)) {
      nodes.get(node.parent)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortAndLevel = (items: CategoryTreeNode[], level = 0) => {
    items.sort(byDefaultOrder);
    items.forEach(item => {
      item.level = level;
      sortAndLevel(item.children, level + 1);
    });
  };

  sortAndLevel(roots);
  return roots;
};

export const normalizeCategoryTree = (categories: Category[], level = 0): CategoryTreeNode[] => {
  return [...categories].sort(byDefaultOrder).map(category => ({
    ...category,
    parent: category.parent ?? null,
    level,
    children: normalizeCategoryTree(category.children || [], level + 1),
  }));
};

export const flattenCategoryTree = (
  categories: CategoryTreeNode[],
  expandedIds?: Set<number>
): CategoryTreeNode[] => {
  const rows: CategoryTreeNode[] = [];
  categories.forEach(category => {
    rows.push(category);
    if (!expandedIds || expandedIds.has(category.id)) {
      rows.push(...flattenCategoryTree(category.children, expandedIds));
    }
  });
  return rows;
};
